import React from 'react';
import { cx } from '@/styled-system/css';
import * as styles from './tabs.styles';
import type { TabsProps } from './types';

export const Tabs = ({
  tabs,
  activeTabId,
  onTabChange,
  ariaLabel,
  idPrefix = 'tab',
  className,
  tabClassName,
}: TabsProps) => {
  const enabledTabs = tabs.filter((t) => !t.disabled);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const currentIndex = enabledTabs.findIndex((t) => t.id === activeTabId);
    let nextIndex: number | null = null;

    if (e.key === 'ArrowRight') nextIndex = (currentIndex + 1) % enabledTabs.length;
    if (e.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + enabledTabs.length) % enabledTabs.length;
    if (e.key === 'Home') nextIndex = 0;
    if (e.key === 'End') nextIndex = enabledTabs.length - 1;

    if (nextIndex !== null) {
      e.preventDefault();
      const nextTab = enabledTabs[nextIndex];
      onTabChange(nextTab.id);
      document.getElementById(`${idPrefix}-${nextTab.id}`)?.focus();
    }
  };

  return (
    <div
      className={cx(styles.tabsContainer, className)}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
    >
      {tabs.map((tab) => {
        const isActive = activeTabId === tab.id;
        const isDisabled = !!tab.disabled;

        return (
          <button
            key={tab.id}
            id={`${idPrefix}-${tab.id}`}
            role="tab"
            aria-selected={isActive}
            aria-disabled={isDisabled || undefined}
            aria-controls={`${idPrefix}panel-${tab.id}`}
            className={cx(styles.tab, isActive && styles.tabActive, isDisabled && styles.tabDisabled, tabClassName)}
            onClick={() => {
              if (!isDisabled) {
                onTabChange(tab.id);
              }
            }}
            tabIndex={isActive ? 0 : -1}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};
